import { useState, useEffect, useMemo, useCallback } from 'react';
import { useApi } from '../Components/Store/ApiContext';
import {
  sortTransactions,
  getDateRange,
  generateTransactionExport
} from '../utils/transactionReportUtils';

export const useIncomeOutcomeReport = () => {
  const { getData } = useApi();
  const [allTransactions, setAllTransactions] = useState([]);
  const [stats, setStats] = useState({
    totalIncome: 0,
    totaloutcome: 0,
    netBalance: 0,
    totalTransactions: 0
  });
  const [methodTotals, setMethodTotals] = useState({});
  const [userTotals, setUserTotals] = useState({});
  const [categoryTotals, setCategoryTotals] = useState([]);
  const [uniqueUsers, setUniqueUsers] = useState([]);
  const [uniquePaymentMethods, setUniquePaymentMethods] = useState([]);
  const [uniqueCategories, setUniqueCategories] = useState([]);
  const [uniqueUnits, setUniqueUnits] = useState([]);
  
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [filters, setFilters] = useState(() => {
    const dateRange = getDateRange('thisMonth');
    return {
      type: 'all',
      source: 'all',
      dateFrom: dateRange.from,
      dateTo: dateRange.to
    };
  });

  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeQuickFilter, setActiveQuickFilter] = useState('thisMonth');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const itemsPerPage = 20;

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (filters.type && filters.type !== 'all') params.append('type', filters.type);
      if (filters.source && filters.source !== 'all') params.append('source', filters.source);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.searchTerm) params.append('searchTerm', filters.searchTerm);
      if (filters.userName) params.append('userName', filters.userName);
      if (filters.paymentMethod) params.append('paymentMethod', filters.paymentMethod);
      if (filters.categoryId) params.append('categoryId', filters.categoryId);
      if (filters.minAmount) params.append('minAmount', filters.minAmount);
      if (filters.maxAmount) params.append('maxAmount', filters.maxAmount);
      if (filters.unitId) params.append('unitId', filters.unitId);

      const endpoint = `report/incomeOutcome${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await getData(null, endpoint);

      if (response && response.results) {
        setAllTransactions(response.results.transactions || []);
        setStats(response.results.stats || { totalIncome: 0, totaloutcome: 0, netBalance: 0, totalTransactions: 0 });
        setMethodTotals(response.results.methodTotals || {});
        setUserTotals(response.results.userTotals || {});
        setCategoryTotals(response.results.categoryTotals || {});
        setUniqueUsers(response.results.uniqueUsers || []);
        setUniquePaymentMethods(response.results.uniquePaymentMethods || []);
        setUniqueCategories(response.results.uniqueCategories || []);
        setUniqueUnits(response.results.uniqueUnits || []);
      }
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setIsLoading(false);
    }
  }, [getData, filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredTransactions = useMemo(() => {
    return sortTransactions(allTransactions, sortBy, sortOrder);
  }, [allTransactions, sortBy, sortOrder]);

  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const paginatedTransactions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredTransactions.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredTransactions, currentPage, itemsPerPage]);

  const hasActiveFilters = useMemo(() => {
    return !!(
      filters.searchTerm ||
      (filters.type && filters.type !== 'all') ||
      (filters.source && filters.source !== 'all') ||
      filters.dateFrom ||
      filters.dateTo ||
      filters.userName ||
      filters.paymentMethod ||
      filters.categoryId ||
      filters.minAmount ||
      filters.maxAmount ||
      filters.unitId
    );
  }, [filters]);

  const handleRefresh = () => {
    loadData();
  };

  const handleExport = () => {
    setIsExporting(true);
    try {
      const xmlData = generateTransactionExport(
        filteredTransactions,
        stats,
        methodTotals,
        userTotals,
        categoryTotals
      );
      const blob = new Blob([xmlData], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `income-outcome-report-${new Date().toISOString().split('T')[0]}.xml`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting data:', error);
    } finally {
      setTimeout(() => setIsExporting(false), 1000);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const handleQuickDateFilter = (range) => {
    const dateRange = getDateRange(range);
    setFilters(prev => ({
      ...prev,
      dateFrom: dateRange.from,
      dateTo: dateRange.to
    }));
    setActiveQuickFilter(range);
    setCurrentPage(1);
  };

  const handleUpdateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({ type: 'all', source: 'all' });
    setActiveQuickFilter('');
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return {
    allTransactions: filteredTransactions,
    paginatedTransactions,
    stats,
    methodTotals,
    userTotals,
    categoryTotals,
    uniqueUsers,
    uniquePaymentMethods,
    uniqueCategories,
    uniqueUnits,

    isLoading,
    isExporting,

    filters,
    sortBy,
    sortOrder,
    currentPage,
    totalPages,
    itemsPerPage,
    activeQuickFilter,
    showAdvancedFilters,
    hasActiveFilters,

    handleRefresh,
    handleExport,
    handleSort,
    handleQuickDateFilter,
    handleUpdateFilter,
    handleClearFilters,
    handlePageChange,
    setShowAdvancedFilters
  };
};
